<template lang="pug">
  b-modal#cropper-modal(
    ref='modal'
    :title="$t('ui.cropImage')"
    @shown='initCropper'
    hide-footer
    centered
    @hide="onCropModalHide"
    no-close-on-backdrop
    size='lg')
    div(v-if='imageSrc')
      img.img-fluid(ref='image' :src='imageSrc' style="max-width: 100%;")
    .mt-3.text-right
      b-button(variant='secondary' @click='cancelCrop') {{ $t('ui.cancel') }}
      b-button.ml-2(variant='primary' @click='confirmCrop') {{ $t('ui.crop') }}
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

export default {
  props: {
    imageSrc: {
      type: String,
      default: null
    },
    originalWidth: {
      type: Number,
      required: true
    },
    originalHeight: {
      type: Number,
      required: true
    },
    originalFileName: {
      type: String,
      default: null
    },
    originalFileType: {
      type: String,
      default: null
    }
  },
  data: () => ({
    cropper: null,
    imageWidth: 0,
    imageHeight: 0
  }),
  beforeDestroy() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    this.imageWidth = 0
    this.imageHeight = 0
  },
  methods: {
    show() {
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      this.imageWidth = 0
      this.imageHeight = 0
    },
    initCropper() {
      this.$nextTick(() => {
        const image = this.$refs.image

        const onLoad = () => {
          this.imageWidth = image.naturalWidth
          this.imageHeight = image.naturalHeight

          if (this.cropper) {
            this.cropper.destroy()
            this.cropper = null
          }

          this.cropper = new Cropper(image, {
            aspectRatio: 1,
            viewMode: 1,
            dragMode: 'move',
            autoCropArea: 1,
            movable: true,
            zoomable: true,
            scalable: true,
            cropBoxResizable: false,
            cropBoxMovable: false,
            ready: () => {
              this.cropper.setCropBoxData({
                width: 512,
                height: 512
              })

              const containerData = this.cropper.getContainerData();

              const cropBoxWidth = 512;
              const cropBoxHeight = 512;

              // Set crop box size and position (centered)
              this.cropper.setCropBoxData({
                width: cropBoxWidth,
                height: cropBoxHeight,
                left: (containerData.width - cropBoxWidth) / 2,
                top: (containerData.height - cropBoxHeight) / 2
              });

              // zoom small images and center canvas
              if (this.originalWidth < 512 || this.originalHeight < 512) {
                setTimeout(() => {
                  this.cropper.zoomTo(1.5);

                  // Center the image canvas
                  const updatedImageData = this.cropper.getImageData();
                  this.cropper.setCanvasData({
                    left: (containerData.width - updatedImageData.width) / 2,
                    top: (containerData.height - updatedImageData.height) / 2,
                    width: updatedImageData.width,
                    height: updatedImageData.height
                  });
                }, 100);
              }
            }
          })
        }

        // Check if the image has already finished loading (e.g., from cache or it's a very small file)
        if (image.complete) {
          onLoad();
        } else {
          // If not yet loaded, assign onLoad to be called when the image finishes loading
          image.onload = onLoad;
        }
      })
    },
    confirmCrop() {
      const cropSize = Math.min(512, this.imageWidth, this.imageHeight)

      // Use Cropper.js to get a cropped canvas with the specified dimensions
      // The crop area is scaled to fit into cropSize x cropSize
      const canvas = this.cropper.getCroppedCanvas({
        width: cropSize,
        height: cropSize
      })

      // Convert the cropped canvas to a base64 data URL
      // Use the original file type to preserve format (e.g., image/jpeg, image/png)
      const dataURL = canvas.toDataURL(this.originalFileType);

      // Convert the data URL into a File object
      // This allows the cropped image to be uploaded or processed like a normal file
      // It reuses the original file's name for consistency
      const file = this.dataURLtoFile(dataURL, this.originalFileName)

      this.$emit('crop-complete', file, dataURL);
      this.hide()
    },
    dataURLtoFile(dataURL, filename) {
      const arr = dataURL.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], filename, { type: mime });
    },
    onCropModalHide(evt) {
      if(evt) {
        const trigger = evt.trigger;
        if (evt && trigger === 'headerclose') {
          this.cancelCrop();
        }
      }
    },
    cancelCrop() {
      this.hide()
      this.$emit('crop-cancel')
    }
  }
};
</script>

<style scoped>
/* .img-fluid {
  max-width: 100%;
  max-height: 500px;
} */
</style>